import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  BusinessIcon,
  BuildingIcon,
  WaterDropIcon,
  WrenchIcon,
  CheckIcon,
  MoneyIcon,
  TrophyIcon
} from '../components/Icons';
import './Services.css';

const ShopCarousel = ({ slides = [], shopLink }) => {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIndex((i) => (i + 1) % slides.length);

  useEffect(() => {
    if (isMobile) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 3500);
    return () => clearInterval(id);
  }, [isMobile, slides.length]);

  if (!slides.length) return null;

  if (isMobile) {
    return (
      <div className="shop-carousel">
        <div className="product-grid">
          {slides.map((item, idx) => (
            <a
              className="carousel-card"
              key={idx}
              href={item.link || shopLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="carousel-img-wrap">
                <img src={item.src} alt={item.alt} loading="lazy" />
              </div>
              <div className="carousel-info">
                <div className="carousel-title">{item.alt}</div>
                <span className="btn btn-primary carousel-shop-btn">Shop Now</span>
              </div>
            </a>
          ))}
        </div>
        {shopLink && (
          <a
            href={shopLink}
            className="btn btn-outline carousel-collection-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Shop Collection
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="shop-carousel">
      <div className="carousel-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {slides.map((item, idx) => (
          <a
            className="carousel-card"
            key={idx}
            href={item.link || shopLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="carousel-img-wrap">
              <img src={item.src} alt={item.alt} loading="lazy" />
            </div>
            <div className="carousel-info">
              <div className="carousel-title">{item.alt}</div>
              <span className="btn btn-primary carousel-shop-btn">Shop Now</span>
            </div>
          </a>
        ))}
      </div>
      <div className="carousel-nav">
        <button onClick={prev} aria-label="Previous product">‹</button>
        <button onClick={next} aria-label="Next product">›</button>
      </div>
      <div className="carousel-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === index ? 'active' : ''}`}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>
      {shopLink && (
        <a
          href={shopLink}
          className="btn btn-outline carousel-collection-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          Shop Collection
        </a>
      )}
    </div>
  );
};

const carouselSlides = {
  waterShop: [
    { src: '/images/1000-Litres-Per-Hour-Water-Purification-System-On-Brackish-Water-scaled.webp', alt: 'Premium water shop counter layout', link: 'https://watershopssa.co.za/products/1000-litres-per-hour-water-purification-system-on-brackish-water' },
    { src: '/images/1000-Litres-Per-Hour-Standard-Water-Purification-System-scaled.webp', alt: 'Bottling bay with RO tanks', link: 'https://watershopssa.co.za/products/1000-litres-per-hour-standard-water-purification-system' },
    { src: '/images/500-Litres-Per-Hour-Standard-Water-Purification-Plant-scaled.webp', alt: 'Pure Drop store equipment bay', link: 'https://watershopssa.co.za/products/500-litres-per-hour-standard-water-purification-plant' },
    { src: '/images/500-Litres-Per-Hour-Water-Purification-Plant-On-Brackish-Water-scaled.webp', alt: 'Point-of-sale and refill area', link: 'https://watershopssa.co.za/products/500-litres-per-hour-water-purification-plant-on-brackish-water' },
    { src: '/images/250-Litres-Per-Hour-Water-Purification-Plant-On-Brackish-Water-scaled.webp', alt: 'Exterior signage concept', link: 'https://watershopssa.co.za/products/250-litres-per-hour-water-purification-plant-on-brackish-water' },
    { src: 'images/250-Litres-Per-Hour-Standard-Water-Purification-System-scaled.webp', alt: 'Technicians staging installations', link: 'https://watershopssa.co.za/products/250-litres-per-hour-standard-water-purification-system' }
  ],
  homeRo: [
    { src: '/images/HRshopify1.webp', alt: 'Under-sink RO installation', link: 'https://watershopssa.co.za/products/cfk-50-with-pump-with-stand' },
    { src: '/images/HRshopify2.webp', alt: 'Designer RO faucet detail', link: 'https://watershopssa.co.za/products/cfk-50-with-pump' },
    { src: '/images/HRshopify3.webp', alt: 'Compact RO membrane stack', link: 'https://watershopssa.co.za/products/cfk-50-without-pump' },
    { src: '/images/HRshopify4.webp', alt: 'Whole-home filtration rack', link: 'https://watershopssa.co.za/products/cfk-50-without-pump-with-stand' },
    { src: '/images/HRshopify5.webp', alt: 'Inline remineralization concept', link: 'https://watershopssa.co.za/products/1600gpd-semi-commercial-ro-system' },
    { src: '/images/HRshopify6.webp', alt: 'RO monitoring panel art', link: 'https://watershopssa.co.za/products/800gpd-domestic-reverse-osmosis-system-for-use-in-your-home' }
  ],
  commercial: [
    { src: '/images/CCPshopify1.webp', alt: 'Commercial RO skid render', link: 'https://watershopssa.co.za/products/1000-gpd-ro-system' },
    { src: '/images/CCPshopify2.webp', alt: 'PLC automation station', link: 'https://watershopssa.co.za/products/advanced-compact-eco-2000gpd-ro-system' },
    { src: '/images/CCPshopify3.webp', alt: 'High capacity membrane rack', link: 'https://watershopssa.co.za/products/2000gpd-ro-system' },
    { src: '/images/CCPshopify4.webp', alt: 'Bulk storage and blending tanks', link: 'https://watershopssa.co.za/products/compact-4000gpd-ro' },
    { src: '/images/CCPshopify5.webp', alt: 'Containerized layout concept', link: 'https://watershopssa.co.za/products/ksro-series-reverse-osmosis-system' },
    { src: '/images/CCPshopify6.webp', alt: 'SCADA visualization', link: 'https://watershopssa.co.za/products/compact-4000gpd-ro-with-permeate-flush' }
  ],
  ice: [
    { src: '/images/iceshopify1.webp', alt: 'Cube ice machines lineup', link: 'https://watershopssa.co.za/products/ice-machine-sk-350p' },
    { src: '/images/iceshopify2.webp', alt: 'Ice storage bin and bagger', link: 'https://watershopssa.co.za/products/ice-machine-sk-420p' },
    { src: '/images/iceshopify3.webp', alt: 'Production floor plan', link: 'https://watershopssa.co.za/products/ice-machine-sk-500p' },
    { src: '/images/iceshopify4.webp', alt: 'RO-fed ice maker install', link: 'https://watershopssa.co.za/products/ice-machine-sk-700p' },
    { src: '/images/iceshopify5.webp', alt: 'Ice plant concept art', link: 'https://watershopssa.co.za/products/ice-machine-sk-1000p' },
    { src: '/images/iceshopify6.webp', alt: 'Retail ice display idea', link: 'https://watershopssa.co.za/products/680kg-of-ice-in-a-24hr-period-ice-making-machine' }
  ],
  office: [
    { src: '/images/offshopify1.webp', alt: 'Pure Drop support team', link: 'https://watershopssa.co.za/products/water-dispenser-ylrx5-2-198b-black-patern' },
    { src: '/images/offshopify2.webp', alt: 'Office dispensers with alkaline water', link: 'https://watershopssa.co.za/products/hot-cold-ylr-lw-2-5-40lbs-floor-standing' },
    { src: '/images/offshopify3.webp', alt: 'Countertop dispenser lineup', link: 'https://watershopssa.co.za/products/hot-cold-m2-floor-standing' },
    { src: '/images/offshopify4.webp', alt: 'Animated water background', link: 'https://watershopssa.co.za/products/hot-cold-m5-floor-standing' },
    { src: '/images/offshopify5.webp', alt: 'Office hydration station concept', link: 'https://watershopssa.co.za/products/water-dispenser-hot-cold-by553' },
    { src: '/images/offshopify6.webp', alt: 'Meeting room dispenser sketch', link: 'https://watershopssa.co.za/products/water-dispenser-hot-cold-by533' }
  ]
};

const Services = () => {
  return (
    <div className="services-page">
      <Helmet>
        <title>Water Services - Water Shop Installations & Purification Plants</title>
        <meta
          name="description"
          content="Professional water shop installations, home RO, borehole systems, commercial plants, alkaline systems, bottling lines, ice machines, and office solutions across South Africa."
        />
      </Helmet>

      {/* Hero Section — compact */}
      <section className="services-hero section small-hero">
        <div className="container">
          <div className="services-hero-content">
            <div className="services-text">
              <h1>Complete Water Solutions</h1>
              <p className="lead">
                Design, supply, install, train, and support—turnkey services for shops, houses, and plants across South Africa.
              </p>
              <div className="hero-cta">
                <Link to="/request" className="btn btn-primary">Get Custom Quote</Link>
                <Link to="#services-list" className="btn btn-outline">View Services</Link>
              </div>
            </div>
            <div className="services-visual" aria-hidden="true">
              <div className="services-icon-badge">
                <WaterDropIcon size={56} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section id="services-list" className="main-services section">
        <div className="container">
          <div className="services-container">

            {/* 1) Water Shop Installations + 4 picture placeholders (cube via existing shop-layout) */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <div className="service-icon">
                    <BusinessIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Water Shop Installations</h2>
                  <p className="service-description">
                    Turnkey water retail setups—compliance, equipment, branding, and training included.
                  </p>

                  <div className="service-features">
                    <h4>What’s Included</h4>
                    <ul>
                      <li><CheckIcon size={16} className="check-icon" /> SANS 241-aligned design & compliance guidance</li>
                      <li><CheckIcon size={16} className="check-icon" /> RO purification line, UV, remineralization (as required)</li>
                      <li><CheckIcon size={16} className="check-icon" /> Filling, capping, sealing & labeling solutions</li>
                      <li><CheckIcon size={16} className="check-icon" /> POS setup & operational SOPs</li>
                      <li><CheckIcon size={16} className="check-icon" /> Staff training and launch support</li>
                    </ul>
                  </div>

                  <div className="service-benefits">
                    <h4>Why It Works</h4>
                    <ul>
                      <li><MoneyIcon size={16} className="list-icon" /> Low-to-medium CAPEX with healthy margins</li>
                      <li><TrophyIcon size={16} className="list-icon" /> Proven retail model with repeat customers</li>
                      <li><WrenchIcon size={16} className="list-icon" /> Scalable equipment and add-ons as you grow</li>
                    </ul>
                  </div>
                </div>

                <div className="service-visual">
                  <div className="visual-box">
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS1.jpeg" alt="Retail water shop counter concept" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS2.jpeg" alt="Bottling racks and filling points illustration" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS3.jpeg" alt="Interior layout for a Pure Drop store" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS4.jpeg" alt="Equipment bay with RO skids preview" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2) Water Shop — Shopify embedded products placeholder (like homepage) */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <h3>Water Shops</h3>
                  <p className="service-description">
                    Browse equipment, filters, consumables, and accessories for your water shop.
                  </p>
                </div>
                <div className="service-visual">
                  <div className="visual-box">
                    <ShopCarousel
                      slides={carouselSlides.waterShop}
                      shopLink="https://watershopssa.co.za/collections/watershops"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 3) Home RO Installation + 4 picture placeholders */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <div className="service-icon">
                    <WaterDropIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Home RO Installation</h2>
                  <p className="service-description">
                    Compact, efficient under-sink and whole-home systems for clean, great-tasting water.
                  </p>

                  <div className="visual-box home-ro-gallery">
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="images/HRinstall1.jpeg" alt="Under-sink RO install mockup" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/HRinstall2.webp" alt="Designer faucet and remin tap example" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/HRinstall3.webp" alt="Compact RO system diagram" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/HRinstall4.jpeg" alt="Whole-home filtration loop illustration" loading="lazy" />
                      </div>
                    </div>
                  </div>

                  <div className="service-features">
                    <h4>Typical Package</h4>
                    <ul>
                      <li><CheckIcon size={16} className="check-icon" /> 5–6 stage RO with remineralization (as needed)</li>
                      <li><CheckIcon size={16} className="check-icon" /> Dedicated faucet + optional fridge/icemaker feed</li>
                      <li><CheckIcon size={16} className="check-icon" /> Professional installation and pressure checks</li>
                      <li><CheckIcon size={16} className="check-icon" /> Maintenance schedule & filter plan</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 4) Home RO — Shopify embedded products placeholder */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <h3>Home RO Products</h3>
                  <p className="service-description">
                    Domestic RO kits, faucets, tanks, and replacement cartridges.
                  </p>
                </div>
                <div className="service-visual">
                  <div className="visual-box">
                    <ShopCarousel
                      slides={carouselSlides.homeRo}
                      shopLink="https://watershopssa.co.za/pages/home-installations"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 5) Borehole Installation (info block, no gallery requested) */}
            <div className="service-detail">
              <div className="service-content reverse">
                <div className="service-visual">
                  <div className="visual-box">
                    <h3>Notes</h3>
                    <div className="shop-layout">
                      <div className="layout-item">As-built docs</div>
                      <div className="layout-item">Spare kit list</div>
                      <div className="layout-item">Lab tests</div>
                      <div className="layout-item">Maintenance plan</div>
                    </div>
                  </div>
                </div>

                <div className="service-text">
                  <div className="service-icon">
                    <WrenchIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Borehole Installation</h2>
                  <p className="service-description">
                    End-to-end borehole water solutions—from source to safe, reliable taps.
                  </p>

                  <div className="service-features">
                    <h4>Scope of Work</h4>
                    <ul>
                      <li><CheckIcon size={16} className="check-icon" /> Site assessment & hydro-geological coordination</li>
                      <li><CheckIcon size={16} className="check-icon" /> Drilling partner coordination, casing & well development</li>
                      <li><CheckIcon size={16} className="check-icon" /> Pump sizing (submersible/booster), controls & protection</li>
                      <li><CheckIcon size={16} className="check-icon" /> Storage: tanks, pressure sets, VSD options</li>
                      <li><CheckIcon size={16} className="check-icon" /> Treatment for iron, manganese, hardness & microbes</li>
                      <li><CheckIcon size={16} className="check-icon" /> Lab testing & commissioning with quality certificate</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 6) Commercial Plant + 4 picture placeholders */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <div className="service-icon">
                    <BuildingIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Commercial Purification Plants</h2>
                  <p className="service-description">
                    High-volume containerized or on-skid systems for wholesale and industrial use.
                  </p>

                  <div className="service-features">
                    <h4>Capabilities</h4>
                    <ul>
                      <li><CheckIcon size={16} className="check-icon" /> RO/UF/UV, remin & blending</li>
                      <li><CheckIcon size={16} className="check-icon" /> PLC/SCADA automation & remote monitoring</li>
                      <li><CheckIcon size={16} className="check-icon" /> Brackish or municipal feed with recovery optimization</li>
                      <li><CheckIcon size={16} className="check-icon" /> 5,000 L/day to 100,000+ L/day</li>
                    </ul>
                  </div>
                </div>

                <div className="service-visual">
                  <div className="visual-box">
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="/images/CPPlant1.png" alt="Containerized purification plant render" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/CClant2.png" alt="SCADA and automation overview graphic" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/CCPlant3.png" alt="Large-scale RO rack illustration" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/CPPlant4.png" alt="High-volume storage and blending tanks concept" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 7) Commercial Plant — Shopify embedded products placeholder */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <h3>Commercial Plant Products</h3>
                  <p className="service-description">
                    High-pressure pumps, membranes, skids, PLCs, and accessories.
                  </p>
                </div>
                <div className="service-visual">
                  <div className="visual-box">
                    <ShopCarousel
                      slides={carouselSlides.commercial}
                      shopLink="https://watershopssa.co.za/pages/reverse-osmosis-systems"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 8) Alkaline Water Systems + 4 picture placeholders */}
            <div className="service-detail">
              <div className="service-content reverse">
                <div className="service-visual">
                  <div className="visual-box">
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="/images/Alkaline1.png" alt="Alkaline filter train concept" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/Alkaline2.png" alt="Mineral cartridge lineup illustration" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/Alkaline3.png" alt="Retail alkaline experience mockup" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/Alkaline4.png" alt="Inline monitoring and pH display graphic" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-text">
                  <div className="service-icon">
                    <WaterDropIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Alkaline Water Systems</h2>
                  <p className="service-description">
                    Premium systems targeting pH 8.5–9.5, mineralization, and antioxidant ORP.
                  </p>

                  <div className="service-features">
                    <h4>Why Choose Alkaline</h4>
                    <ul>
                      <li><CheckIcon size={16} className="check-icon" /> Differentiated retail product with premium pricing</li>
                      <li><CheckIcon size={16} className="check-icon" /> Mineral balancing for taste and mouthfeel</li>
                      <li><CheckIcon size={16} className="check-icon" /> Inline monitoring for consistent quality</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 9) Bottling of Water + 4 picture placeholders */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <div className="service-icon">
                    <BusinessIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Bottling of Water</h2>
                  <p className="service-description">
                    Line design for rinsing, filling, capping, labeling, and date coding.
                  </p>

                  <div className="service-features">
                    <h4>Line Elements</h4>
                    <ul>
                      <li><CheckIcon size={16} className="check-icon" /> Manual to fully automatic lines</li>
                      <li><CheckIcon size={16} className="check-icon" /> PET/Glass options, cap types & shrink sleeves</li>
                      <li><CheckIcon size={16} className="check-icon" /> QA checks, batch coding & traceability</li>
                    </ul>
                  </div>
                </div>

                <div className="service-visual">
                  <div className="visual-box">
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="/images/bottling1.jpg" alt="Bottle rinsing and filling layout" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/bottling2.jpg" alt="Capper and shrink tunnel concept" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/bottling3.jpg" alt="QC station and coding table sample" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/bottling4.jpg" alt="Conveyorized PET line visual" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 10) Ice Machines for Ice Shops + 4 picture placeholders */}
            <div className="service-detail">
              <div className="service-content reverse">
                <div className="service-visual">
                  <div className="visual-box">
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="/images/ice1.jpg" alt="Cube ice machine render" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/ice2.png" alt="Ice bin and bagging station mockup" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/ice3.png" alt="Water treatment feeding ice production" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/ice4.jpg" alt="Shop layout for ice retail" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="service-text">
                  <div className="service-icon">
                    <BuildingIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Ice Machines for Ice Shops</h2>
                  <p className="service-description">
                    Cube, flake, nugget—pair with RO and storage for crystal-clear ice.
                  </p>

                  <div className="service-features">
                    <h4>Spec & Design</h4>
                    <ul>
                      <li><CheckIcon size={16} className="check-icon" /> Production sizing (kg/day) to match demand</li>
                      <li><CheckIcon size={16} className="check-icon" /> Water pre-treatment and filtration</li>
                      <li><CheckIcon size={16} className="check-icon" /> Bins, conveyors, and hygienic handling</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 11) Ice Machines — Shopify embedded products placeholder */}
            <div className="service-detail">
              <div className="service-content">
                <div className="service-text">
                  <h3>Ice Machine Products</h3>
                  <p className="service-description">
                    Machines, bins, filtration kits, and spare parts.
                  </p>
                </div>
                <div className="service-visual">
                  <div className="visual-box">
                    <ShopCarousel
                      slides={carouselSlides.ice}
                      shopLink="https://watershopssa.co.za/pages/ice-making-business"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 12) Office — Shopify embedded products placeholder */}
            <div className="service-detail">
              <div className="service-content reverse">
                <div className="service-visual">
                  <div className="visual-box">
                    <ShopCarousel
                      slides={carouselSlides.office}
                      shopLink="https://watershopssa.co.za/collections/water-dispensers"
                    />
                  </div>
                </div>

                <div className="service-text">
                  <div className="service-icon">
                    <BusinessIcon size={64} className="service-icon-svg" />
                  </div>
                  <h2>Office Water Solutions</h2>
                  <p className="service-description">
                    Dispenser rentals or purchase, bottle delivery, and service plans.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 13) Choose the Right Solution — card-by-card blocks */}
      <section className="service-comparison section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Choose the Right Solution</h2>
            <p>Quick guide to align capacity, budget, and use-case.</p>
          </div>

          {/* Card 1 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Water Shop</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> Low–Medium</li>
                  <li><strong>Setup Time:</strong> 1–2 weeks</li>
                  <li><strong>Daily Capacity:</strong> 500–2,000 L</li>
                  <li><strong>Best For:</strong> Retail refill</li>
                  <li><strong>Key Add-Ons:</strong> POS, branding</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Home RO</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> Low</li>
                  <li><strong>Setup Time:</strong> 1–3 days</li>
                  <li><strong>Daily Capacity:</strong> 200–600 L</li>
                  <li><strong>Best For:</strong> Households</li>
                  <li><strong>Key Add-Ons:</strong> Remineralization, tap kit</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Borehole</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> Medium</li>
                  <li><strong>Setup Time:</strong> 1–3 weeks*</li>
                  <li><strong>Daily Capacity:</strong> As designed</li>
                  <li><strong>Best For:</strong> Farms/Estates</li>
                  <li><strong>Key Add-Ons:</strong> Filtration train</li>
                </ul>
                <p className="muted small">* Depends on drillers, geology, and permitting.</p>
              </div>
            </div>
          </div>

          {/* Card 4 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Commercial Plant</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> High</li>
                  <li><strong>Setup Time:</strong> 4–8+ weeks</li>
                  <li><strong>Daily Capacity:</strong> 5,000–100,000+ L</li>
                  <li><strong>Best For:</strong> Wholesale/Industrial</li>
                  <li><strong>Key Add-Ons:</strong> SCADA, QA lab</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card 5 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Alkaline</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> Medium</li>
                  <li><strong>Setup Time:</strong> 2–3 weeks</li>
                  <li><strong>Daily Capacity:</strong> 1,000–5,000 L</li>
                  <li><strong>Best For:</strong> Premium retail</li>
                  <li><strong>Key Add-Ons:</strong> pH monitor</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card 6 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Bottling</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> Medium–High</li>
                  <li><strong>Setup Time:</strong> 2–6 weeks</li>
                  <li><strong>Daily Capacity:</strong> Up to line spec</li>
                  <li><strong>Best For:</strong> Packaged sales</li>
                  <li><strong>Key Add-Ons:</strong> Coding/labels</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card 7 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Ice Machines</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> Medium</li>
                  <li><strong>Setup Time:</strong> 3–10 days</li>
                  <li><strong>Daily Capacity:</strong> 100–10,000 kg ice</li>
                  <li><strong>Best For:</strong> Ice retail/HoReCa</li>
                  <li><strong>Key Add-Ons:</strong> Bins, RO pre-treat</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card 8 */}
          <div className="service-detail">
            <div className="service-content">
              <div className="service-text">
                <h3>Office</h3>
                <ul className="dash-list">
                  <li><strong>Investment:</strong> Low</li>
                  <li><strong>Setup Time:</strong> 1–3 days</li>
                  <li><strong>Daily Capacity:</strong> N/A</li>
                  <li><strong>Best For:</strong> Workplaces</li>
                  <li><strong>Key Add-Ons:</strong> Service plan</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="table-cta mt-3">
            <Link to="/request" className="btn btn-primary">Get a Recommendation</Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start?</h2>
            <p>We’ll spec the right system, install it, and support you end-to-end.</p>
            <div className="cta-buttons">
              <Link to="/request" className="btn btn-primary">Get Custom Quote</Link>
              <Link to="/how-it-works" className="btn btn-outline">See How It Works</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
